import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, TrendingDown, DollarSign, Percent, Loader2, Wallet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { JobCostsManager } from "@/components/finances/JobCostsManager";
import { QuickBooksConnectCard } from "@/components/finances/QuickBooksConnectCard";
import { AIFinancialAdvisorCard } from "@/components/finances/AIFinancialAdvisorCard";

export default function Financials() {
  const { orgId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("job_profitability" as any)
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(100);
      setRows((data as any[]) || []);
      setLoading(false);
    })();
  }, [orgId]);

  const totalRevenue = rows.reduce((s, r) => s + Number(r.revenue || 0), 0);
  const totalCosts = rows.reduce((s, r) => s + Number(r.total_costs || 0), 0);
  const totalCommissions = rows.reduce((s, r) => s + Number(r.total_commissions || 0), 0);
  const grossProfit = totalRevenue - totalCosts - totalCommissions;
  const margin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

  const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  const top = [...rows].sort((a, b) => Number(b.gross_profit) - Number(a.gross_profit)).slice(0, 5);
  const bottom = [...rows].filter(r => Number(r.revenue) > 0).sort((a, b) => Number(a.margin_percent) - Number(b.margin_percent)).slice(0, 5);

  if (!orgId) return null;

  return (
    <div className="container mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-primary-glow">
          <Wallet className="w-6 h-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Financials</h1>
          <p className="text-muted-foreground">Job profitability, cost tracking, and AI analysis</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Kpi label="Revenue (paid)" value={fmt(totalRevenue)} icon={<DollarSign className="w-4 h-4 text-green-500" />} />
            <Kpi label="Total costs" value={fmt(totalCosts)} icon={<TrendingDown className="w-4 h-4 text-red-500" />} />
            <Kpi label="Commissions" value={fmt(totalCommissions)} icon={<DollarSign className="w-4 h-4 text-yellow-500" />} />
            <Kpi label="Gross profit" value={fmt(grossProfit)} icon={<TrendingUp className="w-4 h-4 text-primary" />} sub={`${margin.toFixed(1)}% margin`} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />Top profitable jobs
              </h3>
              <JobTable rows={top} fmt={fmt} />
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-500" />Lowest-margin jobs
              </h3>
              <JobTable rows={bottom} fmt={fmt} showMargin />
            </Card>
          </div>

          <AIFinancialAdvisorCard orgId={orgId} />
          <QuickBooksConnectCard orgId={orgId} />
          <JobCostsManager orgId={orgId} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon, sub }: { label: string; value: string; icon: React.ReactNode; sub?: string }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm text-muted-foreground">{label}</p>
        {icon}
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function JobTable({ rows, fmt, showMargin }: { rows: any[]; fmt: (n: number) => string; showMargin?: boolean }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground py-6 text-center">No data yet.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Job</TableHead>
          <TableHead className="text-right">Revenue</TableHead>
          <TableHead className="text-right">Profit</TableHead>
          <TableHead className="text-right">Margin</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.job_id}>
            <TableCell className="font-medium">{r.job_number || "—"}</TableCell>
            <TableCell className="text-right">{fmt(Number(r.revenue))}</TableCell>
            <TableCell className="text-right">{fmt(Number(r.gross_profit))}</TableCell>
            <TableCell className="text-right">
              <Badge variant={Number(r.margin_percent) >= 25 ? "default" : Number(r.margin_percent) >= 10 ? "secondary" : "destructive"}>
                {Number(r.margin_percent).toFixed(1)}%
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
